import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { default_design } from '../../static/default_design';
import { PentaName } from '../Penta/PentaName';
import { PentaGuide, visiblePentaNarrativeIds } from '../Penta/PentaGuide';
import { PublicSocial } from '../PublicSocial';
import Social from '../Social';
import { Avatar } from '../UiKit/Avatar';
import { Background } from '../UiKit/Background';
import { Button } from '../UiKit/Button';
import { PentaShare } from '../UiKit/PentaShare';
import { Pop } from '../UiKit/Pop';
import { TooltipH } from '../UiKit/TooltipH';
import { Topbar } from '../UiKit/Topbar';
import { MobGuide } from '../UiKit/MobGuide';
import { applyPentaMemberOrder } from '../utilsPenta';
import { parseAvatar } from '../utilsDesign';
import { VideoPop } from '../VideoPop';
import { PentaPages, visiblePentaPages } from './PentaPages';
import { Study } from './Study';
import { Cross, CrossCompat } from './Cross';

const getHashParams = (hash = window.location.hash) =>
  new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

const setHashParam = (key, value) => {
  const params = getHashParams();
  params.delete('compare');
  params.delete('composite');
  if (value) params.set(key, value);
  else params.delete(key);
  const nextHash = params.toString();
  if (window.location.hash.replace(/^#/, '') === nextHash) return;
  window.location.hash = nextHash;
};

const readPentaNarrativeFromHash = (hash) => {
  const params = getHashParams(hash);
  const hashN = params.get('n');
  if (hashN === 'compare' || hashN === 'composite') return hashN;
  if (params.has('compare')) return 'compare';
  if (params.has('composite')) return 'composite';
  return null;
};

export function PentaView(props) {

  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ui = context.$ui;

  const {
    penta,
    parentContainer,
    mode = 'hd',
  } = { ...props };

  const bodyContainer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const processPenta = (penta) => {
    const ordered = applyPentaMemberOrder(penta);
    const { members, ...rest } = ordered;
    return {
      ...JSON.parse(JSON.stringify(rest)),
      members: (members || []).map(userAvatar => ({
        ...userAvatar,
        avatar: parseAvatar({
          ...userAvatar.avatar,
          design: userAvatar.avatar?.design?.profile ? userAvatar.avatar.design : default_design
        })
      }))
    }
  }
  const [pentaInner, setPentaInner] = useState(
    processPenta(penta)
  )
  const [sharePop, setSharePop] = useState(false);
  const [compare, setCompare] = useState(false);

  const availableNarrativeIds = visiblePentaNarrativeIds(context.pentaNarratives, {
    version: context.state.version,
    memberCount: pentaInner?.members?.length ?? 0,
  });
  const currentNarrativeId = availableNarrativeIds.includes(context.pentaNarrativeCurrent)
    ? context.pentaNarrativeCurrent
    : availableNarrativeIds[0];
  const pentaNarrative = context.pentaNarratives?.[currentNarrativeId]
    || context.pentaNarratives?.composite;
  const pentaNarrativeVisible = pentaNarrative
    ? {
      ...pentaNarrative,
      pages: visiblePentaPages(pentaNarrative.pages, {
        memberCount: pentaInner?.members?.length ?? 0,
        version: context.state.version,
        jyotishShown: context.state.jyotishShown,
      }),
    }
    : null;


  useEffect(() => {
    setPentaInner(processPenta(penta))
  }, [penta])

  useEffect(() => {
    if ((pentaInner?.members?.length ?? 0) >= 3 && compare) {
      setCompare(false);
    }
  }, [pentaInner?.members?.length, compare]);


  useEffect(() => {
    const languageParam = getHashParams(location.hash).get('ln');
    if (languageParam !== context.language && context.languages.includes(languageParam)) {
      context.setLanguage(languageParam);
    }
  }, [location]);


  useEffect(() => {
    if (!availableNarrativeIds.length) return;

    if (pentaInner.members.length > 2 && availableNarrativeIds.includes('compare')) {
      if (context.pentaNarrativeCurrent !== 'compare') {
        context.setPentaNarrativeCurrent('compare');
      }
      return;
    }

    const fromHash = readPentaNarrativeFromHash(location.hash);
    if (fromHash && availableNarrativeIds.includes(fromHash) && fromHash !== context.pentaNarrativeCurrent) {
      context.setPentaNarrativeCurrent(fromHash);
      return;
    }

    if (!availableNarrativeIds.includes(context.pentaNarrativeCurrent)) {
      context.setPentaNarrativeCurrent(availableNarrativeIds[0]);
    }
  }, [location.hash, pentaInner.members.length, availableNarrativeIds.join(',')]);

  useEffect(() => {
    if (!currentNarrativeId) return;
    setHashParam('n', currentNarrativeId);
  }, [currentNarrativeId]);



  useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      if (position > 40) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    // Attach the scroll event listener to the window
    window.addEventListener('scroll', handleScroll);

    // Clean up the event listener
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const scrollable = useRef(null);


  const handleCloseInfoPop = () => {
    document.body.classList.remove('noscroll');
    context.setState.setHighlighted()
    context.setState.setSearch([])

    const removeStudySegments = (pathname) => {
      const segments = pathname.split('/study')
      return segments.length > 1 ? segments[0] : null
    };
    const currentPath = location.pathname;
    const newPath = removeStudySegments(currentPath);
    if (newPath) navigate(newPath);
  };
  const isStudy = location.pathname.includes('study');

  // const pentaDesign = pentaInner.pentaDesign || getPentaDesign(pentaInner)
  // const pentaDesign = pentaInner.pentaDesign || default_design;
  const pentaDesign = pentaInner.pentaDesign;

  const memberCount = pentaInner?.members?.length ?? 0;
  const isCompareNarrative = currentNarrativeId === 'compare' || (memberCount < 3 && compare);
  const pentaClass = [
    !isCompareNarrative && memberCount < 3 ? 'x_edu0--penta' : '',
    isCompareNarrative && memberCount < 3 ? 'x_edu0--composite' : '',
    memberCount > 2 ? 'x_edu0--group' : '',
    isStudy ? 'x_edu0--study' : '',
    pentaNarrative ? 'x_edu0--mob-guide' : '',
    pentaNarrative && context.me ? 'x_edu0--mob-guide-menu' : '',
    pentaNarrative && memberCount > 2 ? 'x_edu0--mob-guide-always' : '',
  ].filter(Boolean).join(' ');

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    section && section.scrollIntoView({ behavior: 'smooth' });
  };


  return (
    <>

      <div>
        {sharePop
          &&
          <Pop
            title={$ui('people_composite')}
            // icon='person_add'
            handleClose={() => setSharePop()}
          >

            <PentaShare penta={penta} handleClose={() => setSharePop()} />

          </Pop>}

        <VideoPop />

        {/* {context.me && <MobMenu penta={penta} />} */}

        <div className={
          (!context.state.peoplePanel ? "x_layout" : "x_layout x_layout--people")
          + (isStudy ? ' x_layout--study' : '')
        }

          onClick={() => handleCloseInfoPop()}
        >




          <Topbar
            // user={user}
            // avatar={avatar}
            penta={pentaInner}
            mode2={scrolled}
            mode={mode}
          />


          <Study
            penta={pentaInner}
            mode={mode}
            // user={user ? user : null}
            // avatar={avatar}
            handleClose={() => navigate('../')}
          />


          {Boolean(context.me) && <Social
            hidePreviews={penta.members.length > 2}
            mode={mode}
          />}
          {!Boolean(context.me) && <PublicSocial hidePreviews={penta.members.length > 3} />}



          {Boolean(penta) && penta.pentaDesign &&
            <>
              {/* <div className={!context.state.peoplePanel ? "x_edu_block x_edu_block--penta" : "x_edu_block x_edu_block--penta x_edu_block--open"}> */}
              <div className="x_edu_c">
                <div className="l_panel" >
                  {/* <div className={`x_edu0 ${isStudy ? 'x_edu0--study' : ''}`} ref={scrollable}> */}

                  <div className={`x_edu0 ${pentaClass}`} ref={scrollable}>

                    <div className={!context.state.peoplePanel ? "x_edu_block x_edu_block--penta" : "x_edu_block x_edu_block--penta x_edu_block--open x_edu_block--penta"}>


                      <div className="x_0_user">
                        <div className="x_0_user_block">

                          {/* <div className="x_0_penta_avas">
                      <div className="x_0_pentatop_1">
                        <Avatar
                          onClick={() => 1}
                          avatar={penta.members[0].avatar}
                          type='calc'
                        />
                      </div>
                      <Avatar
                        onClick={() => 1}
                        penta={penta}
                        type='calc'
                      />
                      <div className="x_0_pentatop_1">
                        <Avatar
                          onClick={() => 1}
                          avatar={penta.members[1] ? penta.members[1].avatar : { design: {} }}
                          type='calc'
                        />
                      </div>
                    </div> */}

                          <div className="x_0_penta_avas">
                            <div className="x_calc_result_name0">



                              <div className="x_calc_result_name ">
                                <Avatar
                                  onClick={() => void (0)}
                                  penta={pentaInner}
                                  type='calc'
                                  tooltip={false}
                                />

                                <PentaName
                                  penta={pentaInner}
                                  flat={true}
                                />

                              </div>
                            </div>


                            {!Boolean(penta.token)
                              && <Button
                                sm
                                iconDesktopOnly
                                icon="save"
                                labelMobileOff
                                iconMobileOnly
                                onClick={() => context.x.createPenta(
                                  pentaInner.members.map(one => one.avatar.token),
                                  (newPenta) => navigate(`/p/${newPenta.token}`)
                                )}
                              >
                                {$ui('penta_save_composite')}
                              </Button>}


                            <div className="x_penta_actions _f _fgap1 _fm">
                              {Boolean(pentaInner.token)
                                && penta.members.length > 1
                                && <Button
                                  ghost
                                  onClick={() => setSharePop(true)}
                                  icon='qr_code'
                                  labelMobileOff
                                >
                                  {$ui('but_public_link_short')}
                                </Button>}

                            </div>





                          </div>

                        </div>
                      </div>

                      <>

                        <div className="x_edu_content00 x_penta_2cols x_edu_penta_persons0">
                          <PentaPages
                            penta={pentaInner}
                            narrative={pentaNarrativeVisible}
                            compare={compare}
                            setCompare={setCompare}
                            callback={(newPenta) => setPentaInner(processPenta(newPenta))}
                          />
                        </div>
                      </>
                    </div>

                  </div>


                </div>
              </div>
              {/* </div> */}
            </>
          }


          <Background
            aura={false}
            style={{
              // zIndex: -1, 
              position: 'fixed'
            }}
            design={pentaDesign}
            theme
          />

          {Boolean(pentaNarrativeVisible) && (
            <>
              {memberCount <= 2 && (
                <PentaGuide
                  narrative={pentaNarrativeVisible}
                  memberCount={memberCount}
                  scrollToSection={scrollToSection}
                />
              )}
              <MobGuide
                narrative={pentaNarrativeVisible}
                hasMobMenu={Boolean(context.me)}
                always={memberCount > 2}
              />
            </>
          )}


        </div>


      </div>



    </>
  )
}

